import apiClient from "../lib/api-client";
import { Supplier } from "./supplier.service";

function mutationKey(storageKey: string): string {
  const existing = sessionStorage.getItem(storageKey);
  if (existing) return existing;

  const key = crypto.randomUUID();
  sessionStorage.setItem(storageKey, key);
  return key;
}

function mutationHeaders(storageKey: string) {
  return { headers: { "Idempotency-Key": mutationKey(storageKey) } };
}

function completeMutation(storageKey: string): void {
  sessionStorage.removeItem(storageKey);
}

export type POStatus =
  | "DRAFT"
  | "SUBMITTED"
  | "PENDING_APPROVAL"
  | "APPROVED"
  | "SENT"
  | "ACKNOWLEDGED"
  | "PARTIAL_RECEIVED"
  | "RECEIVED"
  | "SHORT_CLOSED"
  | "CANCELLED";

export interface PurchaseOrderItem {
  id: string;
  productId: string;
  product: {
    id: string;
    name: string;
    sku: string;
    barcode?: string;
    unit: string;
    manufacturer?: string;
    hsnCode?: string;
    mrp?: number;
  };
  quantity: number;
  freeQuantity: number;
  freeReceivedQty: number;
  freeCancelledQty: number;
  unitPrice: number;
  mrp?: number;
  discountPercent: number;
  discountAmount: number;
  schemeDiscountPercent?: number;
  schemeDiscountAmount: number;
  taxableAmount?: number;
  taxPercent: number;
  taxAmount: number;
  totalAmount: number;
  receivedQty: number;
  cancelledQty: number;
  unitsPerPack?: number;
  baseQuantity?: number;
  purchasePackId?: string;
  purchasePack?: {
    id: string;
    packDescription: string;
    packType: string;
    unitsPerPack: number;
  };
}

export interface PriceDeviationAlert {
  productId: string;
  productName: string;
  contractedBaseUnitPrice: number;
  orderedBaseUnitPrice: number;
  allowedDeviationPercent: number;
  deviationPercent: number;
}

export interface PurchaseOrder {
  id: string;
  tenantId: string;
  storeId: string;
  supplierId: string;
  supplier: Supplier | { id: string; name: string; phone?: string };
  createdById: string;
  poNumber: string;
  poDate: string;
  expectedDelivery?: string;
  subtotal: number;
  discountAmount: number;
  taxAmount: number;
  totalAmount: number;
  status: POStatus;
  notes?: string;
  termsConditions?: string;
  createdAt: string;
  updatedAt: string;
  approvedAt?: string;
  sentAt?: string;
  submittedAt?: string;
  acknowledgedAt?: string;
  shortClosedAt?: string;
  revisionNumber: number;
  approvalPolicy?: Record<string, unknown>;
  approvalRequestId?: string;
  receivedAt?: string;
  items: PurchaseOrderItem[];
  goodsReceipts?: {
    id: string;
    grnNumber: string;
    grnDate: string;
    totalAmount: number;
    status: string;
  }[];
  _count?: {
    items: number;
    goodsReceipts: number;
  };
  pricingAlerts?: PriceDeviationAlert[];
}

export interface PurchaseOrderItemDto {
  productId: string;
  quantity: number;
  freeQuantity?: number;
  unitPrice: number;
  mrp?: number;
  discountPercent?: number;
  schemeDiscountPercent?: number;
  taxPercent?: number;
  purchasePackId?: string;
  unitsPerPack?: number;
}

export interface CreatePurchaseOrderDto {
  supplierId: string;
  storeId: string;
  poDate?: string;
  expectedDelivery?: string;
  items: PurchaseOrderItemDto[];
  discountAmount?: number;
  notes?: string;
  termsConditions?: string;
}

export interface UpdatePurchaseOrderDto {
  poDate?: string;
  expectedDelivery?: string;
  items?: PurchaseOrderItemDto[];
  discountAmount?: number;
  notes?: string;
  termsConditions?: string;
}

export const purchaseOrderService = {
  async getPurchaseOrders(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: POStatus;
    supplierId?: string;
    storeId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<{
    data: PurchaseOrder[];
    meta: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  }> {
    const response = await apiClient.get("/purchase-orders", { params });
    return response.data;
  },

  async getPurchaseOrder(id: string): Promise<PurchaseOrder> {
    const response = await apiClient.get(`/purchase-orders/${id}`);
    return response.data;
  },

  async createPurchaseOrder(
    data: CreatePurchaseOrderDto,
  ): Promise<PurchaseOrder> {
    const storageKey = `medicorepay:purchase-order:create:${data.storeId}:${data.supplierId}`;
    const response = await apiClient.post(
      "/purchase-orders",
      data,
      mutationHeaders(storageKey),
    );
    completeMutation(storageKey);
    return response.data;
  },

  async updatePurchaseOrder(
    id: string,
    data: UpdatePurchaseOrderDto,
  ): Promise<PurchaseOrder> {
    const storageKey = `medicorepay:purchase-order:update:${id}`;
    const response = await apiClient.patch(
      `/purchase-orders/${id}`,
      data,
      mutationHeaders(storageKey),
    );
    completeMutation(storageKey);
    return response.data;
  },

  async updateStatus(
    id: string,
    status: POStatus,
    notes?: string,
  ): Promise<PurchaseOrder> {
    const storageKey = `medicorepay:purchase-order:status:${id}:${status}`;
    const response = await apiClient.patch(
      `/purchase-orders/${id}/status`,
      { status, notes },
      mutationHeaders(storageKey),
    );
    completeMutation(storageKey);
    return response.data;
  },

  async deletePurchaseOrder(id: string): Promise<void> {
    const storageKey = `medicorepay:purchase-order:delete:${id}`;
    await apiClient.delete(
      `/purchase-orders/${id}`,
      mutationHeaders(storageKey),
    );
    completeMutation(storageKey);
  },

  async getPendingItems(supplierId?: string): Promise<
    {
      poId: string;
      poNumber: string;
      poDate: string;
      supplier: { id: string; name: string };
      item: {
        id: string;
        product: { id: string; name: string; sku: string; unit: string };
        orderedQty: number;
        receivedQty: number;
        pendingQty: number;
        freeQuantity: number;
        freeReceivedQty: number;
        freeCancelledQty: number;
        pendingFreeQty: number;
        unitPrice: number;
      };
    }[]
  > {
    const response = await apiClient.get("/purchase-orders/pending-items", {
      params: { supplierId },
    });
    return response.data;
  },

  async emailPurchaseOrder(
    id: string,
    message?: string,
  ): Promise<{ success: boolean; message: string }> {
    const storageKey = `medicorepay:purchase-order:email:${id}`;
    const response = await apiClient.post(
      `/purchase-orders/${id}/email`,
      { message },
      mutationHeaders(storageKey),
    );
    completeMutation(storageKey);
    return response.data;
  },

  async cancelItems(
    id: string,
    items: { itemId: string; cancelQty: number }[],
  ): Promise<PurchaseOrder> {
    const storageKey = `medicorepay:purchase-order:cancel-items:${id}`;
    const response = await apiClient.patch(
      `/purchase-orders/${id}/cancel-items`,
      { items },
      mutationHeaders(storageKey),
    );
    completeMutation(storageKey);
    return response.data;
  },

  async revise(id: string, reason: string): Promise<PurchaseOrder> {
    const storageKey = `medicorepay:purchase-order:revise:${id}`;
    const response = await apiClient.post(
      `/purchase-orders/${id}/revisions`,
      { reason },
      mutationHeaders(storageKey),
    );
    completeMutation(storageKey);
    return response.data;
  },
};

export const PO_STATUS_LABELS: Record<POStatus, string> = {
  DRAFT: "Draft",
  SUBMITTED: "Submitted",
  PENDING_APPROVAL: "Pending Approval",
  APPROVED: "Approved",
  SENT: "Sent to Supplier",
  ACKNOWLEDGED: "Acknowledged",
  PARTIAL_RECEIVED: "Partially Received",
  RECEIVED: "Fully Received",
  SHORT_CLOSED: "Short Closed",
  CANCELLED: "Cancelled",
};

export const PO_STATUS_COLORS: Record<POStatus, string> = {
  DRAFT: "bg-gray-100 text-gray-800",
  SUBMITTED: "bg-cyan-100 text-cyan-800",
  PENDING_APPROVAL: "bg-yellow-100 text-yellow-800",
  APPROVED: "bg-blue-100 text-blue-800",
  SENT: "bg-purple-100 text-purple-800",
  ACKNOWLEDGED: "bg-indigo-100 text-indigo-800",
  PARTIAL_RECEIVED: "bg-orange-100 text-orange-800",
  RECEIVED: "bg-green-100 text-green-800",
  SHORT_CLOSED: "bg-amber-100 text-amber-800",
  CANCELLED: "bg-red-100 text-red-800",
};
